import { useCallback, useMemo, useRef, useState } from 'react';
import type { FormRendererRef } from '@locus-taxy/encore-form';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import type { TranslateFunction } from '@locus-taxy/encore-i18n-toolkit';
import { useUserProfile } from './useUserProfile';
import { useFileUpload } from '../../api/endpoints/files/useFileUpload';
import { usePhotoUrl } from '../../hooks/usePhotoUrl';
import { useLogoutWithO11y } from '../../hooks/useLogoutWithO11y';
import type { ProfileViewMode } from './profileTypes';
import { getGenderLabel } from './profileConstants';
import type { DocumentInfo } from '../../api/endpoints/useCurrentUser';
import type {
  MyProfile,
  MyProfileWork,
  UpdateMyProfileRequest,
} from '../../api/endpoints/useMyProfile';

/**
 * Tri-state pending photo:
 *   undefined → unchanged (keep whatever is stored)
 *   DocumentInfo → pending upload (replace on Save)
 *   null → pending removal (clear on Save)
 */
type PendingPhoto = DocumentInfo | null | undefined;

/** The file id the avatar should show: the pending upload's, or the stored one. */
function resolvePhotoFileId(
  pendingPhoto: PendingPhoto,
  storedFileId: string | null
): string | null {
  if (pendingPhoto === undefined) {
    return storedFileId;
  }
  return pendingPhoto?.fileId ?? null;
}

/**
 * Resolves the photo to submit.
 *
 * The contract is a complete representation, so a save always states the photo: the stored id when
 * it is unchanged (the backend keeps the stored document), the full uploaded document when it was
 * replaced, `null` when it was removed. Omitting it would read as "clear", which is why this never
 * returns undefined.
 */
function resolvePhoto(
  pendingPhoto: PendingPhoto,
  storedFileId: string | null
): DocumentInfo | null {
  if (pendingPhoto === undefined) {
    return storedFileId ? { fileId: storedFileId } : null;
  }
  return pendingPhoto;
}

/**
 * Validates every mounted form. Each one is validated even when an earlier one failed, so the user
 * sees every error at once. A form that is not mounted has no ref — legitimately, when the option
 * lists failed to load and the locale section rendered its error instead — and must not block
 * saving the fields that ARE editable.
 */
function formsAreValid(
  ...refs: Array<{ current: FormRendererRef | null }>
): boolean {
  const results = refs.map(ref =>
    ref.current ? Boolean(ref.current.validate()?.isValid) : true
  );
  return results.every(Boolean);
}

/**
 * The complete editable set, built from the edited forms with the stored profile as the fallback.
 * The body is a COMPLETE representation, not a patch: every field is stated on every save, because
 * an omitted field would read as "clear this".
 */
function buildProfileUpdate(input: {
  personalInfoFormData: Record<string, unknown>;
  localeFormData: Record<string, unknown>;
  profile: MyProfile;
  storedTimezone: string | null;
  storedLocale: string | null;
  photo: DocumentInfo | null;
}): {
  request: UpdateMyProfileRequest;
  timezone: string | null;
  locale: string | null;
} {
  const personal = editedBlock(
    input.personalInfoFormData,
    'personal',
    input.profile.personal
  );
  const contact = editedBlock(
    input.personalInfoFormData,
    'contact',
    input.profile.contact
  );
  const preferences = editedBlock(
    input.localeFormData,
    'preferences',
    input.profile.preferences
  );
  const { timezone, locale } = chosenLocale(
    preferences,
    input.storedTimezone,
    input.storedLocale
  );

  return {
    request: {
      identity: { photo: input.photo },
      contact: { phone: textOrNull(contact?.phone) },
      personal: {
        gender: textOrNull(personal?.gender),
        dateOfBirth: textOrNull(personal?.dateOfBirth),
      },
      preferences: {
        timezone: timezone || null,
        localeIdentifier: locale || null,
      },
    },
    timezone,
    locale,
  };
}

/** A block from the edited form, or the stored one when that form was not touched. */
function editedBlock(
  formData: Record<string, unknown>,
  key: string,
  stored: object | null | undefined
): Record<string, unknown> | undefined {
  return (
    (formData[key] as Record<string, unknown> | undefined) ??
    (stored as Record<string, unknown> | null | undefined) ??
    undefined
  );
}

function textOrNull(value: unknown): string | null {
  return (value as string | undefined) ?? null;
}

/**
 * The timezone and locale the save will store: the edited values, else the stored ones. The locale
 * form starts from the stored values (see LocaleSettingsSection), so the display fallback (client default or
 * browser) never reaches here unless the user picks the same value.
 */
function chosenLocale(
  preferences: Record<string, unknown> | undefined,
  storedTimezone: string | null,
  storedLocale: string | null
): { timezone: string | null; locale: string | null } {
  return {
    timezone: (preferences?.timezone as string | undefined) ?? storedTimezone,
    locale:
      (preferences?.localeIdentifier as string | undefined) ?? storedLocale,
  };
}

/** The Work block's display values; an absent block reads as empty. */
function workFields(
  work: MyProfileWork | null | undefined,
  t: TranslateFunction
) {
  const {
    roleName,
    accessGroupLabel,
    accessGroupNames,
    allAccessGroups,
    skills,
    joiningDate,
  } = work ?? ({} as Partial<MyProfileWork>);
  const label = accessGroupLabel ?? t('dashboard.userDetails.teams', 'Teams');
  return {
    roleName: roleName ?? '',
    accessDimensionLabel: label,
    /* Access to every group must not read like access to none, which is also an empty list. */
    accessDimensionNames: allAccessGroups
      ? t('dashboard.userDetails.allAccessGroups', 'All {{label}}', { label })
      : (accessGroupNames ?? []).join(', '),
    skillNames: (skills ?? []).join(', '),
    joiningDate: joiningDate ?? null,
  };
}

/** The gender's localised label, or the stored value when this page has no label for it. */
function genderLabel(gender: string | null | undefined, t: TranslateFunction) {
  return gender ? getGenderLabel(t, gender) : null;
}

/** A bare ISO date formatted for the caller's locale; the raw value if it cannot be formatted. */
function formatDateOfBirth(dob: string | null | undefined, locale: string) {
  if (!dob) {
    return null;
  }
  try {
    const [y, m, d] = dob.split('-').map(Number);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(y, m - 1, d));
  } catch {
    return dob;
  }
}

/** What the view renders before the profile has loaded: every value empty. */
const NO_PROFILE_BLOCKS: {
  identity: Partial<MyProfile['identity']>;
  contact: Partial<MyProfile['contact']>;
  personal: Partial<MyProfile['personal']>;
} = { identity: {}, contact: {}, personal: {} };

/** The personal block as the view mode renders it. */
function personalViewFields(
  profile: MyProfile | undefined,
  t: TranslateFunction,
  locale: string
) {
  const { identity, contact, personal } = profile ?? NO_PROFILE_BLOCKS;
  return [
    {
      label: t('dashboard.userDetails.fullName', 'Full Name'),
      value: identity.displayName ?? null,
    },
    {
      label: t('dashboard.userDetails.emailAddress', 'Email Address'),
      value: contact.email ?? null,
    },
    {
      label: t('dashboard.userDetails.phoneNumber', 'Phone Number'),
      value: contact.phone ?? null,
    },
    {
      label: t('dashboard.userDetails.employeeId', 'Employee ID'),
      value: identity.employeeId ?? null,
    },
    {
      label: t('dashboard.userDetails.gender', 'Gender'),
      value: genderLabel(personal.gender, t),
    },
    {
      label: t('dashboard.userDetails.dateOfBirth', 'Date of Birth'),
      value: formatDateOfBirth(personal.dateOfBirth, locale),
    },
  ];
}

/**
 * useProfilePage — orchestration for the account page.
 *
 * Manages the view/edit toggle, form refs, dirty state, save and photo handling. Everything that
 * used to reshape entity data for a specific person entity has moved to the backend: there is no
 * merge with the stored record, no admin-managed strip list, and no temporal rewrapping here.
 */
export function useProfilePage() {
  const { t } = useTranslation();
  const {
    profile,
    isLoading,
    error,
    fullName,
    emailAddress,
    photoUrl,
    photoFileId,
    configuredTimezone,
    configuredLocale,
    storedTimezone,
    storedLocale,
    timezoneOptions,
    localeOptions,
    genderOptions,
    canUpdate,
    canEditPhoto,
    editRestrictedReason,
    saveNeedsApproval,
    clientId,
    isUpdating,
    updateProfile,
    refetchProfile,
  } = useUserProfile();

  const [mode, setMode] = useState<ProfileViewMode>('view');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const personalInfoFormRef = useRef<FormRendererRef>(null);
  const localeFormRef = useRef<FormRendererRef>(null);

  const [pendingPhoto, setPendingPhoto] = useState<PendingPhoto>(undefined);
  const { uploadFile, uploading: photoUploading } = useFileUpload(clientId);

  /**
   * The file id currently on screen: the pending upload if there is one, else what is stored.
   * Null once removed, or when the photo is not an Encore file.
   */
  const previewFileId = resolvePhotoFileId(pendingPhoto, photoFileId);

  /**
   * An Encore files URL is an authenticated endpoint, so it cannot go straight into an <img src> —
   * the browser would issue it without the Authorization header and get a 401. usePhotoUrl fetches
   * it as a blob through the API client. A product photo is a plain tenant URL and needs none of
   * that, so it is rendered as-is.
   */
  const { photoUrl: resolvedFilePhotoUrl, loading: photoLoading } = usePhotoUrl(
    {
      fileId: previewFileId,
      clientId,
    }
  );
  const logout = useLogoutWithO11y();

  const [personalInfoFormData, setPersonalInfoFormData] = useState<
    Record<string, unknown>
  >({});
  const [localeFormData, setLocaleFormData] = useState<Record<string, unknown>>(
    {}
  );

  /** The normalised blocks the edit forms bind to, keyed exactly as the form schema keys them. */
  const formInitialData = useMemo(
    () => ({
      identity: profile?.identity ?? {},
      contact: profile?.contact ?? {},
      personal: profile?.personal ?? {},
      preferences: profile?.preferences ?? {},
    }),
    [profile]
  );

  const previewPhotoUrl = useMemo(() => {
    if (previewFileId) {
      return resolvedFilePhotoUrl;
    }
    // No Encore file: either a tenant-hosted URL, or the photo was removed.
    return pendingPhoto === undefined ? photoUrl : null;
  }, [previewFileId, resolvedFilePhotoUrl, pendingPhoto, photoUrl]);

  const workProfileData = useMemo(
    () => workFields(profile?.work, t),
    [profile, t]
  );

  /** True when no person entity backs the caller here — the work block is absent, not empty. */
  const hasWorkProfile = Boolean(profile?.work);

  const personalInfoViewFields = useMemo(
    () => personalViewFields(profile, t, configuredLocale),
    [profile, configuredLocale, t]
  );

  const localeViewFields = useMemo(
    () => [
      {
        label: t('dashboard.userDetails.timezone', 'Timezone'),
        value: configuredTimezone,
      },
      {
        label: t('dashboard.userDetails.language', 'Language'),
        value: configuredLocale,
      },
    ],
    [configuredTimezone, configuredLocale, t]
  );

  const lastUpdated = useMemo(() => {
    if (!profile?.lastUpdated) {
      return null;
    }
    try {
      return new Intl.DateTimeFormat(configuredLocale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: configuredTimezone,
      }).format(new Date(profile.lastUpdated));
    } catch {
      return profile.lastUpdated;
    }
  }, [profile, configuredLocale, configuredTimezone]);

  const handlePersonalInfoChange = useCallback(
    (data: Record<string, unknown>) => {
      setPersonalInfoFormData(data);
      setIsDirty(true);
    },
    []
  );

  const handleLocaleChange = useCallback((data: Record<string, unknown>) => {
    setLocaleFormData(data);
    setIsDirty(true);
  }, []);

  const handlePhotoSelect = useCallback(
    async (file: File) => {
      try {
        const documentInfo: DocumentInfo = await uploadFile(file);
        setPendingPhoto(documentInfo);
        setIsDirty(true);
      } catch {
        // Error already surfaced by uploadFile
      }
    },
    [uploadFile]
  );

  const handlePhotoRemove = useCallback(() => {
    setPendingPhoto(null);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    // Without this, a save with no loaded profile posts every writable field as null — and the
    // action is a full-object replace, so that clears all six.
    if (!profile || !canUpdate) {
      return;
    }

    if (!formsAreValid(personalInfoFormRef, localeFormRef)) {
      return;
    }

    const { request, timezone, locale } = buildProfileUpdate({
      personalInfoFormData,
      localeFormData,
      profile,
      storedTimezone,
      storedLocale,
      // Not writable on every profile; sending a value where it is not would be rejected 400.
      photo: canEditPhoto ? resolvePhoto(pendingPhoto, photoFileId) : null,
    });

    setIsSaving(true);
    try {
      const saved = await updateProfile.mutateAsync(request);
      /* Held for approval: nothing was applied, so there is no new locale to reload into. */
      const applied = !saved?.data?.pendingChangeRequestId;

      await refetchProfile();
      setMode('view');
      setIsDirty(false);
      setPendingPhoto(undefined);

      /* Against what was stored, not the fallback the page displays: an untouched save of
         a record with no preferences sends null back and must not reload. */
      if (applied && (timezone !== storedTimezone || locale !== storedLocale)) {
        window.location.reload();
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    personalInfoFormData,
    localeFormData,
    profile,
    storedTimezone,
    storedLocale,
    pendingPhoto,
    photoFileId,
    canEditPhoto,
    canUpdate,
    updateProfile,
    refetchProfile,
  ]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowUnsavedDialog(true);
    } else {
      setMode('view');
    }
  }, [isDirty]);

  const handleDialogStay = useCallback(() => setShowUnsavedDialog(false), []);

  const handleDialogLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    setMode('view');
    setIsDirty(false);
    setPersonalInfoFormData({});
    setLocaleFormData({});
    setPendingPhoto(undefined);
  }, []);

  const handleEditClick = useCallback(() => {
    setMode('edit');
    setIsDirty(false);
    setPersonalInfoFormData({});
    setLocaleFormData({});
    setPendingPhoto(undefined);
  }, []);

  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  return {
    mode,
    isDirty,
    isSaving: isSaving || isUpdating,
    showUnsavedDialog,
    canUpdate,
    canEditPhoto,
    editRestrictedReason,
    saveNeedsApproval,
    clientId,
    isLoading,
    error,

    profile,
    formInitialData,
    fullName,
    emailAddress,
    configuredTimezone,
    configuredLocale,
    storedTimezone,
    storedLocale,
    timezoneOptions,
    localeOptions,
    genderOptions,
    lastUpdated,

    photoUrl: previewPhotoUrl,
    photoLoading,
    photoUploading,
    hasPhoto:
      pendingPhoto === undefined
        ? Boolean(photoUrl || photoFileId)
        : pendingPhoto !== null,

    workProfileData,
    hasWorkProfile,

    personalInfoViewFields,
    localeViewFields,

    personalInfoFormRef,
    localeFormRef,

    handleEditClick,
    handleCancel,
    handleSave,
    handleDialogStay,
    handleDialogLeave,
    handlePersonalInfoChange,
    handleLocaleChange,
    handlePhotoSelect,
    handlePhotoRemove,
    handleLogout,
  };
}
